/**
 * Creator Resource Kit — Sticky Tab Highlight + Sticky CTA Visibility
 *
 * Two IntersectionObservers:
 *   1. Tracks M3-M7 sections to highlight the matching sticky tab link.
 *   2. Watches the footer (or page bottom sentinel) to fade the sticky CTA.
 *
 * Loaded with `defer` from creator-kit-sticky-tab.liquid because no inline
 * <script> on the page calls into window.CreatorKit. The rule in
 * .claude/rules/frontend-script-loading.md forbids defer only when an
 * adjacent inline script depends on the deferred module — not the case here.
 *
 * Vanilla JS, IIFE — no global pollution beyond window.CreatorKit (read-only).
 */
(function () {
  'use strict';

  // Guard: only initialise once even if section re-renders.
  if (window.CreatorKit && window.CreatorKit.__inited) return;

  var api = {
    __inited: false,
    initStickyTab: initStickyTab,
    initStickyCta: initStickyCta
  };
  window.CreatorKit = api;

  function ready(fn) {
    if (document.readyState !== 'loading') {
      fn();
    } else {
      document.addEventListener('DOMContentLoaded', fn);
    }
  }

  function initStickyTab() {
    var nav = document.querySelector('[data-creator-kit-tab]');
    if (!nav) return;

    var tabLinks = Array.prototype.slice.call(nav.querySelectorAll('[data-tab-target]'));
    if (tabLinks.length === 0) return;

    var targets = tabLinks
      .map(function (link) {
        var id = link.getAttribute('data-tab-target');
        var el = document.getElementById(id);
        return el ? { id: id, el: el, link: link } : null;
      })
      .filter(Boolean);

    if (targets.length === 0) return;

    // Smooth scroll behaviour for anchor clicks while accounting for sticky header.
    tabLinks.forEach(function (link) {
      link.addEventListener('click', function (e) {
        var id = link.getAttribute('data-tab-target');
        var el = document.getElementById(id);
        if (!el) return;
        e.preventDefault();
        var rect = el.getBoundingClientRect();
        var offset = nav.offsetHeight || 0;
        var y = rect.top + window.pageYOffset - offset - 8;
        window.scrollTo({ top: y, behavior: 'smooth' });
        setActive(link);
      });
    });

    if (!('IntersectionObserver' in window)) {
      // Graceful degrade: no auto-highlight, anchor click still works.
      return;
    }

    var observer = new IntersectionObserver(function (entries) {
      // Pick the entry with the highest intersection ratio that is intersecting.
      var visible = entries
        .filter(function (e) { return e.isIntersecting; })
        .sort(function (a, b) { return b.intersectionRatio - a.intersectionRatio; });

      if (visible.length === 0) return;
      var topId = visible[0].target.id;
      var match = targets.find(function (t) { return t.id === topId; });
      if (match) setActive(match.link);
    }, {
      // Trigger highlight when section enters middle band of viewport.
      rootMargin: '-30% 0px -50% 0px',
      threshold: [0, 0.25, 0.5, 0.75, 1]
    });

    targets.forEach(function (t) { observer.observe(t.el); });

    function setActive(link) {
      tabLinks.forEach(function (l) { l.classList.remove('is-active'); });
      link.classList.add('is-active');
    }
  }

  function initStickyCta() {
    var cta = document.querySelector('[data-creator-kit-sticky-cta]');
    if (!cta) return;

    var footer = document.querySelector('.shopify-section-group-footer-group')
      || document.querySelector('footer')
      || document.querySelector('[data-creator-kit-footer-sentinel]');

    if (!footer || !('IntersectionObserver' in window)) {
      // No footer / no IO support → leave CTA visible.
      return;
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          cta.classList.add('is-hidden');
        } else {
          cta.classList.remove('is-hidden');
        }
      });
    }, {
      rootMargin: '0px',
      threshold: 0
    });

    observer.observe(footer);
  }

  ready(function () {
    api.__inited = true;
    initStickyTab();
    initStickyCta();
  });
})();
